/**
 * PropertyInfo Component
 *
 * Main property information header section.
 */

import React from 'react';
import type { ListingData } from '../hooks/useListing';

interface PropertyInfoProps {
  listing: ListingData;
  onSave?: () => void;
  isSaved?: boolean;
}

export const PropertyInfo: React.FC<PropertyInfoProps> = ({
  listing,
  onSave,
  isSaved = false,
}) => {
  return (
    <div className="py-6 border-b border-gray-200">
      {/* Title and Save */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-700 capitalize">
              {listing.propertyType}
            </span>
            {listing.host.isSuperhost && (
              <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                Superhost
              </span>
            )}
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            {listing.title}
          </h1>
        </div>

        <button
          onClick={onSave}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:border-gray-400 transition-colors shrink-0"
        >
          <HeartIcon
            className={`w-5 h-5 ${
              isSaved ? 'fill-red-500 text-red-500' : 'text-gray-600'
            }`}
          />
          <span className="hidden sm:inline text-sm font-medium">
            {isSaved ? 'Saved' : 'Save'}
          </span>
        </button>
      </div>

      {/* Location and Rating */}
      <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
        <span className="text-gray-600">
          {listing.location.city}, {listing.location.area}
        </span>

        {listing.rating && (
          <div className="flex items-center gap-1">
            <StarIcon className="w-4 h-4 fill-yellow-400 text-yellow-400" />
            <span className="font-medium">{listing.rating.toFixed(1)}</span>
            <span className="text-gray-500">
              ({listing.reviewCount} reviews)
            </span>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-gray-600">
        <span className="flex items-center gap-1">
          <GuestsIcon className="w-4 h-4" />
          {listing.maxGuests} guests
        </span>
        <span className="text-gray-300">·</span>
        <span className="flex items-center gap-1">
          <BedroomIcon className="w-4 h-4" />
          {listing.bedrooms} bedroom{listing.bedrooms !== 1 ? 's' : ''}
        </span>
        <span className="text-gray-300">·</span>
        <span className="flex items-center gap-1">
          <BedIcon className="w-4 h-4" />
          {listing.beds} bed{listing.beds !== 1 ? 's' : ''}
        </span>
        <span className="text-gray-300">·</span>
        <span className="flex items-center gap-1">
          <BathroomIcon className="w-4 h-4" />
          {listing.bathrooms} bath{listing.bathrooms !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  );
};

// Icons
const HeartIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const StarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

// Font Awesome style icons
const GuestsIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 640 512">
    {/* Font Awesome Users icon */}
    <path d="M144 0a80 80 0 1 1 0 160A80 80 0 1 1 144 0zM512 0a80 80 0 1 1 0 160A80 80 0 1 1 512 0zM0 298.7C0 239.8 47.8 192 106.7 192h42.7c15.9 0 31 3.5 44.6 9.7c-1.3 7.2-1.9 14.7-1.9 22.3c0 38.2 16.8 72.5 43.3 96H21.3C9.6 320 0 310.4 0 298.7zM405.3 320H384c26.5-23.5 43.3-57.8 43.3-96c0-7.6-.7-15-1.9-22.3c13.6-6.3 28.7-9.7 44.6-9.7h42.7C571.2 192 619 239.8 619 298.7c0 11.8-9.6 21.3-21.3 21.3H405.3zM416 224a96 96 0 1 0 -192 0 96 96 0 1 0 192 0zM128 485.3C128 411.7 187.7 352 261.3 352H378.7C452.3 352 512 411.7 512 485.3c0 14.7-11.9 26.7-26.7 26.7H154.7c-14.7 0-26.7-11.9-26.7-26.7z"/>
  </svg>
);

const BedroomIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    {/* Double bed with headboard icon */}
    <path d="M7 14c1.66 0 3-1.34 3-3S8.66 8 7 8s-3 1.34-3 3 1.34 3 3 3zm0-4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm10 4c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm0-4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM19 2H5C3.9 2 3 2.9 3 4v14c0 1.1.9 2 2 2h2v-3h10v3h2c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 12h-6v-2H5V4h14v10z"/>
  </svg>
);

const BedIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    {/* Single/twin bed icon */}
    <path d="M21 10.78V8c0-1.65-1.35-3-3-3h-4c-.77 0-1.47.3-2 .78-.53-.48-1.23-.78-2-.78H6C4.35 5 3 6.35 3 8v2.78c-.61.55-1 1.34-1 2.22v5h2v-1h16v1h2v-5c0-.88-.39-1.67-1-2.22zM14 7h4c.55 0 1 .45 1 1v2h-6V8c0-.55.45-1 1-1zM5 8c0-.55.45-1 1-1h4c.55 0 1 .45 1 1v2H5V8zm-1 7v-2c0-.55.45-1 1-1h14c.55 0 1 .45 1 1v2H4z"/>
  </svg>
);

const BathroomIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 512 512">
    {/* Font Awesome Bath icon */}
    <path d="M96 77.3c0-7.3 5.9-13.3 13.3-13.3c3.5 0 6.9 1.4 9.4 3.9l14.9 14.9C130 91.8 128 101.7 128 112c0 19.9 7.2 38 19.2 52c-5.3 9.2-4 21.1 3.8 29c9.4 9.4 24.6 9.4 33.9 0L289 89c9.4-9.4 9.4-24.6 0-33.9c-7.9-7.9-19.8-9.1-29-3.8C246 39.2 227.9 32 208 32c-10.3 0-20.2 2-29.2 5.5L163.9 22.6C149.4 8.1 129.7 0 109.3 0C66.6 0 32 34.6 32 77.3V256c-17.7 0-32 14.3-32 32s14.3 32 32 32H480c17.7 0 32-14.3 32-32s-14.3-32-32-32H96V77.3zM32 352v16c0 28.4 12.4 54 32 71.6V480c0 17.7 14.3 32 32 32s32-14.3 32-32V464H384v16c0 17.7 14.3 32 32 32s32-14.3 32-32V439.6c19.6-17.6 32-43.1 32-71.6V352H32z"/>
  </svg>
);

export default PropertyInfo;
